import type {
  CreateMeterAddonDto,
  DriveFolderType,
  OutputTechnology,
  SerializedMeterAddon,
  SerializedMeterModel,
  UpdateMeterAddonDto,
} from '@enmon/commons/lib/tools';
import { BASE_REST_API_URL } from 'common/constants';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import { buildQuery, type Filters, type Populate } from '@enmon/commons/lib/tools/utils/buildQuery';
import { BaseApiClient } from './baseApiClient';
import type { MeterModelFile } from './meterModelsApi';

// NOTE: WE ARE GRADUALLY RENAMING 'METER ADDONS' TO 'METER MODULES'

export type MeterModulesApiFilter = BaseAPIParams<SerializedMeterAddon> & {
  search?: string;
  meterModels?: SerializedMeterModel['id'][];
  technologies?: OutputTechnology[];
};

export type GetMeterModulesArg = {
  filter: MeterModulesApiFilter;
  signal: AbortSignal;
};
export type GetMeterModulesResponse = StrapiApiResponse<SerializedMeterAddon>;

export type GetOneMeterModuleArg = {
  moduleId: SerializedMeterAddon['id'];
  signal: AbortSignal;
  populate?: Populate<SerializedMeterAddon>;
};
export type GetOneMeterModuleResult = SerializedMeterAddon;

export type CreateMeterModuleArg = CreateMeterAddonDto;
export type CreateMeterModuleResult = SerializedMeterAddon;

export type UpdateMeterModuleArg = {
  id: SerializedMeterAddon['id'];
  values: UpdateMeterAddonDto;
};
export type UpdateMeterModuleResult = SerializedMeterAddon;

export type DeleteMeterModuleArg = SerializedMeterAddon['id'];
export type DeleteMeterModuleResult = SerializedMeterAddon;

export type GetMeterModuleFilesArg = {
  moduleId: SerializedMeterAddon['id'];
  folderType: DriveFolderType;
  pageSize?: number;
  resize?: boolean;
};
/** same shape as a model's files: a module's Drive folder has docs/ and photos/ too */
export type GetMeterModuleFilesResult = MeterModelFile[];

export class MeterModulesApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('meter-addons', BASE_REST_API_URL, jwt);
  }

  public async find({ filter, signal }: GetMeterModulesArg): Promise<GetMeterModulesResponse> {
    const { meterModels, search, technologies, populate, page, pageSize, limit, start, sort, fields } = filter ?? {};

    const response = await this.axios.get<GetMeterModulesResponse>('', {
      params: buildQuery<SerializedMeterAddon>({
        filters: {
          ...(search && { name: { $containsi: search } }),
          ...(meterModels && { meter_models: { id: { $in: meterModels } } }),
          ...(technologies && { technology: { $in: technologies } }),
        } as Filters<SerializedMeterAddon>,
        fields,
        populate,
        pagination: {
          page,
          pageSize,
          limit,
          start,
        },
        sort,
      }),
      signal,
    });

    return response.data;
  }

  public async findOne({ moduleId, populate, signal }: GetOneMeterModuleArg): Promise<SerializedMeterAddon> {
    const response = await this.axios.get<{ data: SerializedMeterAddon }>(`${moduleId}`, {
      params: buildQuery<SerializedMeterAddon>({
        filters: {},
        ...(populate && { populate }),
      }),
      signal,
    });

    return response.data.data;
  }

  public async create(values: CreateMeterModuleArg): Promise<CreateMeterModuleResult> {
    const response = await this.axios.post<{ data: CreateMeterModuleResult }>('', { data: values });

    return response.data.data;
  }

  public async update({ id, values }: UpdateMeterModuleArg): Promise<UpdateMeterModuleResult> {
    const response = await this.axios.put<{ data: UpdateMeterModuleResult }>(`${id}`, { data: values });

    return response.data.data;
  }

  public async delete(id: DeleteMeterModuleArg): Promise<DeleteMeterModuleResult> {
    const response = await this.axios.delete<DeleteMeterModuleResult>(`${id}`);

    return response.data;
  }

  public async getFiles({
    moduleId,
    folderType,
    pageSize,
    resize,
    signal,
  }: GetMeterModuleFilesArg & { signal: AbortSignal }): Promise<GetMeterModuleFilesResult> {
    const response = await this.axios.get<GetMeterModuleFilesResult>(`${moduleId}/files`, {
      params: { folderType, ...(pageSize && { pageSize }), ...(resize && { resize }) },
      signal,
    });

    return response.data;
  }

  public async downloadFile({
    moduleId,
    driveFileId,
    signal,
  }: {
    moduleId: SerializedMeterAddon['id'];
    driveFileId: string;
    signal?: AbortSignal;
  }): Promise<Blob> {
    const response = await this.axios.get<Blob>(`${moduleId}/files/${driveFileId}/download`, {
      responseType: 'blob',
      signal,
    });

    return response.data;
  }
}
